// src/auth/apiFetch.js — full replacement
// 401 → sign-out; 403 TENANT_ACCESS_DENIED → picker (or the superadmin banner); 410 TENANT_PURGED → tenantPurged.js.
import { API_BASE_URL, getSessionHeaders } from '../services/api/config.js';
import { noteRefusalIfDrift } from './permissionsDrift.js';
import {
  readTenantPurged,
  notifyTenantPurged,
  setTenantPurgedHandler,
  tenantNamedByRequest,
  tenantPurgedError,
} from './tenantPurged.js';

// Set by AuthContext after mount so apiFetch can trigger navigation.
let _onSessionExpired = null;
let _onTenantDenied = null;
let _onSuperadminTenantBlocked = null;
let _getIsSuperadmin = () => false;
let _getAdminMode = () => false;

// 401s that refuse a CREDENTIAL the request carried, not the session: the caller
// words them and the user stays signed in. A wrong current password in Profilo
// used to end the session.
const CREDENTIAL_REFUSALS = new Set(['AUTH_INVALID_CURRENT_PASSWORD']);

async function isCredentialRefusal(res) {
  try {
    const body = await res.clone().json();
    return CREDENTIAL_REFUSALS.has(body?.error_code);
  } catch {
    return false;
  }
}

export function registerAuthCallbacks({
  onSessionExpired,
  onTenantDenied,
  onSuperadminTenantBlocked,
  onTenantPurged,
  getIsSuperadmin,
  getAdminMode,
} = {}) {
  if (onSessionExpired !== undefined) _onSessionExpired = onSessionExpired;
  if (onTenantDenied !== undefined) _onTenantDenied = onTenantDenied;
  if (onSuperadminTenantBlocked !== undefined) _onSuperadminTenantBlocked = onSuperadminTenantBlocked;
  // The 410 handler lives in tenantPurged.js so the raw-fetch clients reach it too.
  if (onTenantPurged !== undefined) setTenantPurgedHandler(onTenantPurged);
  if (getIsSuperadmin !== undefined) _getIsSuperadmin = getIsSuperadmin;
  if (getAdminMode !== undefined) _getAdminMode = getAdminMode;
}

export function getAuthCallbacks() {
  return { onSessionExpired: _onSessionExpired, onTenantDenied: _onTenantDenied };
}

export async function apiFetch(path, options = {}) {
  const { suppressTenantHeader, ...fetchOptions } = options;
  const token = localStorage.getItem('auth_token');
  const tenantId = localStorage.getItem('auth_tenant_id');

  // For FormData bodies (file upload) we must NOT set Content-Type — the browser
  // sets it to multipart/form-data with the correct boundary. Setting JSON here
  // would corrupt the request.
  const isFormData = typeof FormData !== 'undefined' && fetchOptions.body instanceof FormData;

  // Built once so the 410 branch can read what was actually sent.
  const url = `${API_BASE_URL}${path}`;
  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...getSessionHeaders(),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(!suppressTenantHeader && tenantId ? { 'X-Tenant-Id': tenantId } : {}),
    ...(fetchOptions.headers || {}),
  };
  // ⛔ A request that names its workspace ONLY by X-Tenant-Id has one URL for every workspace, and the browser's HTTP
  // cache keys on the URL alone: the backend's 410 TENANT_PURGED carries no Cache-Control (Chromium keeps a 410
  // indefinitely), so a cached 410 would later answer for ANOTHER workspace and get it dropped — and a 200 with
  // `max-age` would show another workspace's data. Such requests never touch the cache. A tenant-keyed URL
  // (`?tenant=`, a /user-config/tenants/{id}/ path) keeps the backend's deliberate caching. A caller's own `cache` wins.
  const headerOnlyTenant = !!headers['X-Tenant-Id'] && !tenantNamedByRequest(url);
  const res = await fetch(url, { ...(headerOnlyTenant ? { cache: 'no-store' } : {}), ...fetchOptions, headers });

  if (res.status === 401 && !(await isCredentialRefusal(res))) {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_tenants');
    localStorage.removeItem('auth_customers');
    localStorage.removeItem('auth_tenant_id');
    _onSessionExpired?.();
    throw new Error('SESSION_EXPIRED');
  }

  if (res.status === 403) {
    const body = await res.clone().json().catch(() => ({}));

    // Superadmin in admin mode hit a tenant-scoped route by accident: an
    // expected mis-route, not a stale session, so it gets its own banner and is
    // deliberately excluded from the drift check below.
    const saBlocked = body.error_code === 'TENANT_ACCESS_DENIED'
      && _getIsSuperadmin() && _getAdminMode() && !!_onSuperadminTenantBlocked;

    // A refusal that contradicts the claims this browser holds means those
    // claims are stale — `tenants[]` / `customers[]` are minted at login and
    // /auth/me only replays them, so no refetch can repair it. Flag it so the
    // user is offered re-authentication; noteRefusalIfDrift stays silent when
    // the FE and the server already agree. Branch on error_code, never message.
    if (!saBlocked) {
      noteRefusalIfDrift({
        status: 403,
        errorCode: body.error_code,
        details: body.details,
        tenantId,
      });
    }

    if (body.error_code === 'TENANT_ACCESS_DENIED') {
      if (saBlocked) {
        // Don't clear tenant_id; keep adminMode; surface a banner.
        _onSuperadminTenantBlocked();
        throw new Error('SUPERADMIN_TENANT_BLOCKED');
      }
      localStorage.removeItem('auth_tenant_id');
      _onTenantDenied?.();
      throw new Error('TENANT_DENIED');
    }
  }

  // 410 TENANT_PURGED — the workspace is gone for everyone, for ever (auth/tenantPurged.js): drop the one the body
  // names (else the one this request named) and let AuthContext re-route. ⛔ Never drift: noteRefusalIfDrift is
  // 403-only and is not called.
  const purged = res.status === 410 ? await readTenantPurged(res) : null;
  if (purged) {
    notifyTenantPurged(purged.tenantId || tenantNamedByRequest(url, headers));
    throw tenantPurgedError();
  }

  return res;
}
